import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import {
  Table,
  Button,
  Image,
  Badge,
  Checkbox,
  Card,
  Row,
  Col,
  Space,
  Collapse,
  Input,
  Modal,
  Pagination,
  Segmented,
  Tag,
  Tooltip,
  Alert,
} from 'antd';
import {
  FilterOutlined,
  SearchOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getImageFallbackUrl, getMenuId } from '../../utils/helpers';
import { useStaffProductSearch, useCategories, useSubcategories, useBrands } from '../../hooks/useMenuQuery';

const STOCK_FILTER_ALL = 'all';
const STOCK_FILTER_IN = 'in';
const STOCK_FILTER_OUT = 'out';

const IMAGE_FILTER_ALL = 'all';
const IMAGE_FILTER_HAS = 'has';
const IMAGE_FILTER_NONE = 'none';

const QUERY_OPERATORS = ['AND', 'OR', 'NOT'];

function BulkProductSearch() {
  const queryClient = useQueryClient();
  const { setHeaderComponent, selectedLanguage, languages } = useContext(LayoutContext);
  const menuId = getMenuId();

  const { doFetch: bulkOpsFetch } = useFetch();
  const { doFetch: bulkOpsDryRunFetch } = useFetch();

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [stockFilter, setStockFilter] = useState(STOCK_FILTER_ALL);
  const [imageFilter, setImageFilter] = useState(IMAGE_FILTER_ALL);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAllOnPage, setSelectAllOnPage] = useState(false);
  const [applyToAllMatching, setApplyToAllMatching] = useState(false);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [stockModalVisible, setStockModalVisible] = useState(false);
  const [pendingStockAction, setPendingStockAction] = useState(null);
  const [previewMessage, setPreviewMessage] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const searchParams = useMemo(() => {
    const params = {
      page: currentPage,
      per_page: pageSize,
    };
    if (debouncedSearchQuery.trim()) {
      params.q = debouncedSearchQuery.trim();
    }
    if (selectedCategories.length > 0) {
      params.category_ids = selectedCategories;
    }
    if (selectedSubcategories.length > 0) {
      params.subcategory_ids = selectedSubcategories;
    }
    if (selectedBrands.length > 0) {
      params.brand_ids = selectedBrands;
    }
    if (stockFilter === STOCK_FILTER_IN) {
      params.is_in_stock = true;
    } else if (stockFilter === STOCK_FILTER_OUT) {
      params.is_in_stock = false;
    }
    if (imageFilter === IMAGE_FILTER_HAS) {
      params.has_image = true;
    } else if (imageFilter === IMAGE_FILTER_NONE) {
      params.has_image = false;
    }
    return params;
  }, [
    currentPage,
    pageSize,
    debouncedSearchQuery,
    selectedCategories,
    selectedSubcategories,
    selectedBrands,
    stockFilter,
    imageFilter,
  ]);

  const searchFiltersForBulk = useMemo(() => {
    const { page, per_page, ...filters } = searchParams;
    return filters;
  }, [searchParams]);

  const { data: categoriesData } = useCategories(menuId, 1, 100);
  const { data: subcategoriesData } = useSubcategories(menuId, 1, 100);
  const { data: brandsData } = useBrands(menuId, 1, 100);
  const {
    data: productsData,
    isLoading: isLoadingProducts,
    refetch: refetchProducts,
  } = useStaffProductSearch(searchParams);

  const filteredProducts = useMemo(() => {
    if (!productsData?.products) {
      return [];
    }
    const currentLanguage = selectedLanguage || languages?.[0]?.id;
    return productsData.products.map((product) => {
      const primaryTranslation =
        product.product_translations?.find((t) => t.language_id === currentLanguage) ||
        product.product_translations?.[0];
      return {
        ...product,
        name: primaryTranslation?.name_text || 'Unnamed Product',
      };
    });
  }, [productsData?.products, languages, selectedLanguage]);

  const transformedCategories = useMemo(() => {
    if (!categoriesData?.categories || !languages) return [];
    return categoriesData.categories.map((cat) => ({
      id: cat.id,
      name:
        cat.category_translations?.find((t) => t.language_id === selectedLanguage)?.name_text ||
        cat.category_translations?.[0]?.name_text ||
        `Category ${cat.id}`,
    }));
  }, [categoriesData?.categories, languages, selectedLanguage]);

  const transformedSubcategories = useMemo(() => {
    if (!subcategoriesData?.subcategories || !languages) return [];
    return subcategoriesData.subcategories.map((sub) => ({
      id: sub.id,
      name:
        sub.subcategory_translations?.find((t) => t.language_id === selectedLanguage)?.name_text ||
        sub.subcategory_translations?.[0]?.name_text ||
        `Subcategory ${sub.id}`,
    }));
  }, [subcategoriesData?.subcategories, languages, selectedLanguage]);

  const transformedBrands = useMemo(() => {
    if (!brandsData?.brands || !languages) return [];
    return brandsData.brands.map((brand) => ({
      id: brand.id,
      name:
        brand.brand_translations?.find((t) => t.language_id === selectedLanguage)?.name_text ||
        brand.brand_translations?.[0]?.name_text ||
        `Brand ${brand.id}`,
    }));
  }, [brandsData?.brands, languages, selectedLanguage]);

  const totalActiveFilters = useMemo(() => {
    let count =
      selectedCategories.length +
      selectedSubcategories.length +
      selectedBrands.length +
      (debouncedSearchQuery.trim() ? 1 : 0);
    if (stockFilter !== STOCK_FILTER_ALL) count += 1;
    if (imageFilter !== IMAGE_FILTER_ALL) count += 1;
    return count;
  }, [
    selectedCategories.length,
    selectedSubcategories.length,
    selectedBrands.length,
    debouncedSearchQuery,
    stockFilter,
    imageFilter,
  ]);

  useEffect(() => {
    setHeaderComponent(<b>Bulk Product Search</b>);
    return () => setHeaderComponent(null);
  }, [setHeaderComponent]);

  useEffect(() => {
    if (filteredProducts.length > 0) {
      const pageIds = filteredProducts.map((p) => p.id);
      setSelectAllOnPage(pageIds.length > 0 && pageIds.every((id) => selectedProducts.includes(id)));
    } else {
      setSelectAllOnPage(false);
    }
  }, [selectedProducts, filteredProducts]);

  const insertOperator = (operator) => {
    setSearchQuery((prev) => {
      const trimmed = prev.trim();
      if (!trimmed) return `${operator} `;
      return `${trimmed} ${operator} `;
    });
  };

  const toggleFilterId = (setter, id, checked) => {
    setter((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const clearAllFiltersAndSearch = () => {
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setSelectedBrands([]);
    setStockFilter(STOCK_FILTER_ALL);
    setImageFilter(IMAGE_FILTER_ALL);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleSelectAllOnPage = (checked) => {
    if (checked) {
      const pageIds = filteredProducts.map((p) => p.id);
      setSelectedProducts((prev) => [...new Set([...prev, ...pageIds])]);
    } else {
      const pageIds = new Set(filteredProducts.map((p) => p.id));
      setSelectedProducts((prev) => prev.filter((id) => !pageIds.has(id)));
    }
  };

  const buildBulkPayload = useCallback(
    (action) => {
      if (applyToAllMatching) {
        return {
          action,
          apply_to_search: true,
          search_filters: searchFiltersForBulk,
        };
      }
      return {
        action,
        product_ids: selectedProducts,
      };
    },
    [applyToAllMatching, searchFiltersForBulk, selectedProducts],
  );

  const invalidateSearch = () => {
    queryClient.invalidateQueries({ queryKey: ['staff-product-search'] });
    queryClient.invalidateQueries({ queryKey: ['search-products'] });
    refetchProducts();
  };

  const runBulkOperation = (action, { dryRun = false, onSuccess } = {}) => {
    const fetchFn = dryRun ? bulkOpsDryRunFetch : bulkOpsFetch;
    fetchFn({
      url: dryRun ? URLS.bulkProductOperationsDryRun() : URLS.bulkProductOperations(),
      method: 'POST',
      params: buildBulkPayload(action),
      onSuccess: (response) => {
        setPreviewMessage(response?.message || null);
        if (!dryRun) {
          setSelectedProducts([]);
          setApplyToAllMatching(false);
          invalidateSearch();
        }
        onSuccess?.(response);
      },
      successMessage: dryRun ? undefined : responseMessageForAction(action),
      showSuccessNotification: !dryRun,
    });
  };

  const responseMessageForAction = (action) => {
    if (action === 'delete') return 'Products deleted successfully.';
    if (action === 'set_in_stock') return 'Products marked in stock.';
    return 'Products marked out of stock.';
  };

  const handleStockAction = (action) => {
    setPendingStockAction(action);
    setPreviewMessage(null);
    setStockModalVisible(true);
    runBulkOperation(action, { dryRun: true });
  };

  const confirmStockAction = () => {
    if (!pendingStockAction) return;
    runBulkOperation(pendingStockAction, {
      onSuccess: () => {
        setStockModalVisible(false);
        setPendingStockAction(null);
      },
    });
  };

  const handleDeleteClick = () => {
    setPreviewMessage(null);
    setDeleteModalVisible(true);
    runBulkOperation('delete', { dryRun: true });
  };

  const confirmDelete = () => {
    runBulkOperation('delete', {
      onSuccess: () => setDeleteModalVisible(false),
    });
  };

  const selectionCount = applyToAllMatching
    ? productsData?.total || 0
    : selectedProducts.length;

  const canRunBulkAction = applyToAllMatching
    ? (productsData?.total || 0) > 0
    : selectedProducts.length > 0;

  const columns = [
    {
      title: (
        <Checkbox
          checked={selectAllOnPage}
          indeterminate={
            filteredProducts.some((p) => selectedProducts.includes(p.id)) && !selectAllOnPage
          }
          onChange={(e) => handleSelectAllOnPage(e.target.checked)}
          disabled={applyToAllMatching}
        />
      ),
      key: 'selection',
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={selectedProducts.includes(record.id) || applyToAllMatching}
          disabled={applyToAllMatching}
          onChange={(e) =>
            toggleFilterId(setSelectedProducts, record.id, e.target.checked)
          }
        />
      ),
    },
    {
      title: 'Image',
      key: 'image',
      width: 70,
      render: (_, record) => (
        <Image
          src={record.image_url}
          fallback={getImageFallbackUrl(record.image_url)}
          width={48}
          height={48}
          style={{ objectFit: 'cover', borderRadius: '50%' }}
        />
      ),
    },
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Category',
      key: 'category',
      render: (_, record) => {
        const t = record.category?.category_translations?.find(
          (tr) => tr.language_id === selectedLanguage,
        );
        return t?.name_text || record.category?.category_translations?.[0]?.name_text || '—';
      },
    },
    {
      title: 'Brand',
      key: 'brand',
      render: (_, record) => {
        const t = record.brand?.brand_translations?.find(
          (tr) => tr.language_id === selectedLanguage,
        );
        return t?.name_text || record.brand?.brand_translations?.[0]?.name_text || '—';
      },
    },
    {
      title: 'Stock',
      key: 'stock',
      width: 100,
      render: (_, record) =>
        record.is_in_stock ? (
          <Tag color="green">In stock</Tag>
        ) : (
          <Tag color="red">Out of stock</Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Button
          icon={<ExportOutlined />}
          size="small"
          onClick={() => window.open(`/product/${record.id}`, '_blank')}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Search across all languages"
        description={
          <>
            Use checkboxes and buttons below to filter without typing. In the search box you can
            combine terms with <Tag>AND</Tag> <Tag>OR</Tag> <Tag>NOT</Tag> (e.g.{' '}
            <code>yamaha AND piano NOT used</code>).
          </>
        }
      />

      <Card title="Search products" style={{ marginBottom: 16 }}>
        <Input
          placeholder='Search by name in any language (e.g. yamaha AND piano NOT sale)'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefix={<SearchOutlined />}
          allowClear
          style={{ marginBottom: 12 }}
        />
        <Space wrap>
          <span style={{ color: '#666', fontSize: 13 }}>Add to query:</span>
          {QUERY_OPERATORS.map((op) => (
            <Button key={op} size="small" onClick={() => insertOperator(op)}>
              {op}
            </Button>
          ))}
        </Space>
        {debouncedSearchQuery && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#666' }}>
            Active query: <strong>{debouncedSearchQuery}</strong>
            {productsData != null &&
              ` — ${productsData.total} matching product${productsData.total !== 1 ? 's' : ''}`}
          </div>
        )}
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Stock status</div>
            <Segmented
              value={stockFilter}
              onChange={setStockFilter}
              options={[
                { label: 'All', value: STOCK_FILTER_ALL },
                { label: 'In stock', value: STOCK_FILTER_IN, icon: <CheckCircleOutlined /> },
                { label: 'Out of stock', value: STOCK_FILTER_OUT, icon: <CloseCircleOutlined /> },
              ]}
              block
            />
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Product image</div>
            <Segmented
              value={imageFilter}
              onChange={setImageFilter}
              options={[
                { label: 'All', value: IMAGE_FILTER_ALL },
                { label: 'Has image', value: IMAGE_FILTER_HAS },
                { label: 'No image', value: IMAGE_FILTER_NONE },
              ]}
              block
            />
          </Col>
        </Row>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <Collapse
          activeKey={filtersCollapsed ? [] : ['filters']}
          onChange={(keys) => setFiltersCollapsed(keys.length === 0)}
          ghost
        >
          <Collapse.Panel
            key="filters"
            header={
              <Space>
                <FilterOutlined />
                <span>Categories, subcategories & brands</span>
                {totalActiveFilters > 0 && (
                  <Badge count={totalActiveFilters} style={{ backgroundColor: '#1890ff' }} />
                )}
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <h4>Categories</h4>
                <Space direction="vertical" style={{ maxHeight: 220, overflow: 'auto', width: '100%' }}>
                  {transformedCategories.map((cat) => (
                    <Checkbox
                      key={cat.id}
                      checked={selectedCategories.includes(cat.id)}
                      onChange={(e) =>
                        toggleFilterId(setSelectedCategories, cat.id, e.target.checked)
                      }
                    >
                      {cat.name}
                    </Checkbox>
                  ))}
                </Space>
              </Col>
              <Col xs={24} md={8}>
                <h4>Subcategories</h4>
                <Space direction="vertical" style={{ maxHeight: 220, overflow: 'auto', width: '100%' }}>
                  {transformedSubcategories.map((sub) => (
                    <Checkbox
                      key={sub.id}
                      checked={selectedSubcategories.includes(sub.id)}
                      onChange={(e) =>
                        toggleFilterId(setSelectedSubcategories, sub.id, e.target.checked)
                      }
                    >
                      {sub.name}
                    </Checkbox>
                  ))}
                </Space>
              </Col>
              <Col xs={24} md={8}>
                <h4>Brands</h4>
                <Space direction="vertical" style={{ maxHeight: 220, overflow: 'auto', width: '100%' }}>
                  {transformedBrands.map((brand) => (
                    <Checkbox
                      key={brand.id}
                      checked={selectedBrands.includes(brand.id)}
                      onChange={(e) =>
                        toggleFilterId(setSelectedBrands, brand.id, e.target.checked)
                      }
                    >
                      {brand.name}
                    </Checkbox>
                  ))}
                </Space>
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Button onClick={clearAllFiltersAndSearch} disabled={totalActiveFilters === 0}>
                Clear all filters
              </Button>
            </div>
          </Collapse.Panel>
        </Collapse>
      </Card>

      {(selectedProducts.length > 0 || applyToAllMatching) && (
        <Card style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap>
              <span>
                <strong>{selectionCount}</strong> product{selectionCount !== 1 ? 's' : ''} selected
              </span>
              <Checkbox
                checked={applyToAllMatching}
                onChange={(e) => {
                  setApplyToAllMatching(e.target.checked);
                  if (e.target.checked) {
                    setSelectedProducts([]);
                  }
                }}
              >
                <Tooltip title="Apply the action to every product matching the current search, not only this page">
                  All matching search results ({productsData?.total || 0})
                </Tooltip>
              </Checkbox>
              <Button
                size="small"
                onClick={() => {
                  setSelectedProducts([]);
                  setApplyToAllMatching(false);
                }}
              >
                Clear selection
              </Button>
            </Space>
            <Space wrap>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                disabled={!canRunBulkAction}
                onClick={() => handleStockAction('set_in_stock')}
              >
                Mark in stock
              </Button>
              <Button
                icon={<CloseCircleOutlined />}
                disabled={!canRunBulkAction}
                onClick={() => handleStockAction('set_out_of_stock')}
              >
                Mark out of stock
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={!canRunBulkAction}
                onClick={handleDeleteClick}
              >
                Delete selected
              </Button>
            </Space>
          </Space>
        </Card>
      )}

      <Table
        dataSource={filteredProducts}
        columns={columns}
        rowKey="id"
        loading={isLoadingProducts}
        pagination={false}
        scroll={{ x: 700 }}
        size="small"
        locale={{
          emptyText: isLoadingProducts ? 'Loading…' : 'No products match your search',
        }}
      />

      {productsData && productsData.pages > 1 && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Pagination
            current={currentPage}
            total={productsData.total || 0}
            pageSize={pageSize}
            onChange={(page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) setPageSize(size);
            }}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={['10', '20', '30', '50', '100']}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} products`}
          />
        </div>
      )}

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>
              {pendingStockAction === 'set_in_stock' ? 'Mark in stock' : 'Mark out of stock'}
            </span>
          </Space>
        }
        open={stockModalVisible}
        onOk={confirmStockAction}
        onCancel={() => {
          setStockModalVisible(false);
          setPendingStockAction(null);
        }}
        okText="Apply"
      >
        <p>
          You are about to update <strong>{selectionCount}</strong> product
          {selectionCount !== 1 ? 's' : ''}.
        </p>
        {previewMessage && <Alert type="info" message={previewMessage} style={{ marginTop: 12 }} />}
      </Modal>

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Delete products</span>
          </Space>
        }
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete permanently"
        okButtonProps={{ danger: true }}
      >
        <Alert
          type="error"
          showIcon
          message="This cannot be undone easily"
          description={
            <>
              <p>
                You are about to delete <strong>{selectionCount}</strong> product
                {selectionCount !== 1 ? 's' : ''}. Products are archived (soft-deleted) so orders
                that reference them still work, but they will disappear from the live menu until
                restored.
              </p>
              {previewMessage && <p>{previewMessage}</p>}
            </>
          }
        />
      </Modal>
    </div>
  );
}

export default BulkProductSearch;
